import { getFeed, getFeedPage } from 'src/service/engageFrontendGateway';
import { ContentFeedMetadata, ContentMixedListMetadata, PageType } from 'types/publishedAssets';
import { FeedType, FeedSort, FeedSortDirection, FeedItem } from 'types/feeds';
import { getSortAndDirection } from 'components/Content/ContentTypes/Feed/helpers';

export const MIXED_LIST_DEFAULT_PAGE_SIZE = 12;

/** Query params for a mixed-list read: sort_mode date maps to the releasedAt/startTime date sort. */
export const getMixedListSortParams = (
  metadata: ContentMixedListMetadata,
): { sort?: FeedSort; sortDirection?: FeedSortDirection } =>
  metadata.sort_mode === 'date' ? { sort: 'releasedAt', sortDirection: 'desc' } : {};

// Find all feed content blocks in a page (including featured_project which needs project feed data)
export const findFeedContentBlocks = (
  page: PageType,
): Array<{ id: string; metadata: ContentFeedMetadata; content_type: string }> => {
  const feedBlocks: Array<{ id: string; metadata: ContentFeedMetadata; content_type: string }> = [];

  for (const section of page?.sections || []) {
    for (const entity of section?.section_entities || []) {
      if (entity.content_type === 'feed') {
        feedBlocks.push({
          id: entity.id,
          metadata: entity.metadata as ContentFeedMetadata,
          content_type: entity.content_type,
        });
      } else if (entity.content_type === 'featured_project') {
        // Featured projects need project feed data
        feedBlocks.push({
          id: entity.id,
          metadata: { ...entity.metadata, feed_type: 'project' } as ContentFeedMetadata,
          content_type: entity.content_type,
        });
      } else if (entity.content_type === 'mixed_list') {
        // Mixed lists resolve their manual selection server-side by contentId; page 1 is baked for SEO
        feedBlocks.push({
          id: entity.id,
          metadata: entity.metadata as ContentFeedMetadata,
          content_type: entity.content_type,
        });
      }
    }
  }

  return feedBlocks;
};

// Extract feed query parameters from metadata.
// `contentId` (the feed block's content.id) is forwarded ONLY when pin_priority_enabled is set,
// so the backend can order the block's priority pins first. `version` is forwarded alongside it
// for published reads only (callers pass undefined for preview), so the backend orders by the
// frozen per-version pin snapshot rather than the live draft pins. Everything else is unaffected.
export const extractFeedParams = (
  metadata: ContentFeedMetadata,
  contentId?: string,
  version?: string,
): {
  feedType: FeedType;
  queryParams: {
    sort?: FeedSort;
    sortOrder?: FeedSortDirection;
    includeClosed?: boolean;
    includedTagIds?: string[];
    excludedTagIds?: string[];
    statusFilter?: ('open' | 'upcoming' | 'closed')[];
    includedActivityIds?: string[];
    contentId?: string;
    version?: string;
  };
} | null => {
  const {
    feed_type: feedType,
    display_order: displayOrder,
    show_closed_activities: showClosedActivities,
    included_tag_ids: includedTagIds,
    excluded_tag_ids: excludedTagIds,
    status_filter: statusFilter,
    included_activity_ids: includedActivityIds,
    pin_priority_enabled: pinPriorityEnabled,
  } = metadata;

  if (!feedType) {
    return null;
  }

  return {
    feedType: feedType as FeedType,
    queryParams: {
      ...getSortAndDirection(feedType, displayOrder),
      ...(statusFilter?.length ? { statusFilter } : { includeClosed: showClosedActivities }),
      includedTagIds,
      excludedTagIds,
      includedActivityIds,
      ...(pinPriorityEnabled && contentId ? { contentId } : {}),
      ...(pinPriorityEnabled && contentId && version ? { version } : {}),
    },
  };
};

// Fetch a single feed with error handling
export const fetchSingleFeed = async (
  pageId: string,
  contentId: string,
  feedType: FeedType,
  queryParams: {
    locale: string;
    organization?: string;
    sort?: FeedSort;
    sortDirection?: FeedSortDirection;
    includeClosed?: boolean;
    includedTagIds?: string[];
    excludedTagIds?: string[];
    statusFilter?: ('open' | 'upcoming' | 'closed')[];
    includedActivityIds?: string[];
    includedPageIds?: string[];
    contentId?: string;
    version?: string;
    preview?: boolean;
  },
): Promise<FeedItem[]> => {
  try {
    const feed = await getFeed(pageId, feedType, queryParams);

    return feed || [];
  } catch (error) {
    console.error(`Failed to fetch feed for content ${contentId}:`, error);
    return [];
  }
};

// Helper function to fetch feed data for all feed content blocks in the page.
// `version` is the manifest's published version; it is forwarded to the backend for pin ordering
// ONLY on published reads (preview === false). In preview we deliberately omit it so the feed
// reflects the CURRENT draft pins — a previously-published page still carries an old version in its
// manifest, but preview must not order by that stale frozen snapshot.
export const fetchFeedDataForPage = async (
  page: PageType,
  locale: string,
  organization?: string,
  pinOptions?: { preview?: boolean; version?: string },
) => {
  const feedBlocks = findFeedContentBlocks(page);
  const versionForPins = pinOptions?.preview ? undefined : pinOptions?.version;
  // Preview reflects live draft state (e.g. pin order) not encoded in the request URL; flag it so
  // the gateway sends no-store and the CDN/edge can't serve a stale feed until the next publish.
  // Forwarded only when previewing so published reads keep a clean, cacheable request.
  const previewParam = pinOptions?.preview ? { preview: true } : {};

  // Create promises for all feed fetches
  const feedPromises = feedBlocks
    .map(({ id, metadata, content_type: contentType }) => {
      // For featured_project, always fetch project feed
      if (contentType === 'featured_project') {
        return fetchSingleFeed(page.id, id, 'project', {
          locale,
          organization,
          includedPageIds: metadata.project_uuids,
          ...previewParam,
        }).then((feed) => ({ id, feed }));
      }

      // Mixed lists bake page 1 only (load-more fetches the rest client-side). The block's
      // contentId is what identifies the selection; version reads the frozen published snapshot.
      if (contentType === 'mixed_list') {
        const manualMetadata = metadata as ContentMixedListMetadata;
        return getFeedPage(
          page.id,
          'mixed_list',
          {
            locale,
            organization,
            contentId: id,
            ...getMixedListSortParams(manualMetadata),
            ...(versionForPins ? { version: versionForPins } : {}),
            ...previewParam,
          },
          1,
          manualMetadata.page_size ?? MIXED_LIST_DEFAULT_PAGE_SIZE,
        ).then(({ items }) => ({ id, feed: items }));
      }

      // For regular feed content, use existing logic
      const params = extractFeedParams(metadata, id, versionForPins);
      if (!params) return null;

      return fetchSingleFeed(page.id, id, params.feedType, {
        locale,
        organization,
        ...params.queryParams,
        ...previewParam,
      }).then((feed) => ({ id, feed }));
    })
    .filter((promise): promise is Promise<{ id: string; feed: FeedItem[] }> => promise !== null);

  // Fetch all feeds in parallel
  const results = await Promise.all(feedPromises);

  // Convert results array to record
  return results.reduce<Record<string, FeedItem[]>>((acc, { id, feed }) => {
    acc[id] = feed;
    return acc;
  }, {});
};

// Collect all engagement IDs from feeds where feed type is engagement_activity
export const collectEngagementIdsFromFeeds = (page: PageType, feeds: Record<string, FeedItem[]>): string[] => {
  const feedBlocks = findFeedContentBlocks(page);
  const engagementIds = new Set<string>();

  for (const { id, metadata } of feedBlocks) {
    // Only process feeds with engagement_activity type
    if (metadata.feed_type === 'engagement_activity') {
      const feedItems = feeds[id];
      if (feedItems) {
        // Collect all engagement IDs from the feed items
        feedItems.forEach((item) => {
          if (item.id) {
            engagementIds.add(item.id);
          }
        });
      }
    }
  }

  return Array.from(engagementIds);
};
